// controllers/useLobbyController.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLobby } from '@features/lobby/hooks/useLobby.js';
import { getGameSocket } from '@api/socket.js';
import { getPersistentUserId } from '@api/userService.js';
import { logDev } from '@utils/logDev.js';

export function useLobbyController() {
    const { socket, reconnect, joinLobby } = useLobby();
    const { id: lobbyCode } = useParams();
    const persistentId = getPersistentUserId();
    const navigate = useNavigate();

    const [showJoinDialog, setShowJoinDialog] = useState(false);
    const [joinError, setJoinError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [forcePassword, setForcePassword] = useState(false);

    const [users, setUsers] = useState([]);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [userName, setUserName] = useState('');
    const [isCreator, setIsCreator] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [settings, setSettings] = useState({
        mode: 'quiz',
        numberOfQuestions: 5,
        selectedCategories: ['LeagueOfLegends'],
        selectedSubCategories: [],
        difficulty: ['einfach', 'mittel', 'schwer'],
        pixelationStrength: 0.1,
        avoidUsedQuestions: false,
        duration: 15000,
    });

    const game = useMemo(() => getGameSocket(settings.mode), [settings.mode]);
    const hasLeftRef = useRef(false);

    useEffect(() => {
        game.emit('joinGame', { lobbyCode });
    }, [game, lobbyCode]);

    useEffect(() => {
        if (!lobbyCode) {
            setJoinError('Kein Lobby-Code angegeben.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        reconnect({ lobbyCode, persistentId })
            .then(response => {
                if (response.success && !response.reconnectedToGame) {
                    const me = response.users.find(u => u.persistentId === persistentId);
                    if (me) {
                        setUsers(response.users);
                        setUserName(me.name);
                        setIsCreator(me.isCreator);
                    } else {
                        setForcePassword(response.requiresPassword ?? false);
                        setShowJoinDialog(true);
                    }
                } else {
                    if (response.message === 'Lobby nicht gefunden') {
                        setJoinError('Diese Lobby existiert nicht');
                    } else {
                        setJoinError('Fehler beim Lobby-Wiederbeitritt');
                    }
                }
            })
            .finally(() => setIsLoading(false));
    }, [lobbyCode, persistentId]);

    useEffect(() => {
        const onLobbyUpdate = ({ users }) => {
            setUsers(users);
            const me = users.find(u => u.persistentId === persistentId);
            if (me) {
                setUserName(me.name);
                setIsCreator(me.isCreator);
            }
        };
        const onChatMessage = ({ userName, message }) =>
            setMessages(msgs => [...msgs, { userName, message }]);
        socket.on('lobbyUpdate', onLobbyUpdate);
        socket.on('chatMessage', onChatMessage);
        return () => {
            socket.off('lobbyUpdate', onLobbyUpdate);
            socket.off('chatMessage', onChatMessage);
        };
    }, []);

    useEffect(() => {
        const onUserKicked = () => {
            logDev('[Lobby] You were kicked – navigating to home');
            navigate('/', {
                replace: true,
                state: {
                    wasKicked: true,
                    reason: 'Du wurdest aus der Lobby entfernt.'
                }
            });
        };
        socket.on('userKicked', onUserKicked);
        return () => socket.off('userKicked', onUserKicked);
    }, [navigate]);

    useEffect(() => {
        const onGameStarting = ({ gameType, questions, startTimestamp, duration, imageDifficulties, pixelationStrength }) => {
            navigate(`/game/${gameType}/${lobbyCode}`, {
                state: {
                    questions,
                    startTimestamp,
                    duration,
                    imageDifficulties,
                    pixelationStrength,
                    gameType
                }
            });
        };
        game.on('gameStarting', onGameStarting);
        return () => game.off('gameStarting', onGameStarting);
    }, [lobbyCode, game, navigate]);

    const handleJoinDialogSubmit = async ({ name, password }) => {
        if (!name) {
            setJoinError('Name darf nicht leer sein.');
            return;
        }

        try {
            const response = await joinLobby({ lobbyCode, name, persistentId, password: password || null });
            if (response.success) {
                if (response.gameStarted) {
                    navigate(`/game/${response.gameType}/${lobbyCode}`);
                } else {
                    setUsers(response.users);
                    setUserName(name);
                    setShowJoinDialog(false);
                }
            } else {
                setJoinError(response.message || 'Fehler beim Beitreten');
                if (response.requiresPassword) setForcePassword(true);
            }
        } catch (err) {
            logDev('Join-Fehler:', err);
            setJoinError('Unbekannter Fehler beim Beitreten');
        }
    };

    const startGame = () => {
        game.emit('startGame', { lobbyCode, settings }, response => {
            logDev('[Lobby] startGame ACK:', response);
            if (!response.success) logDev('Fehler beim Starten des Spiels:', response.message);
        });
    };

    const sendMessage = () => {
        const trimmed = input.trim();
        if (trimmed) {
            // Einfache Sanitierung: HTML-Tags entfernen, um XSS zu verhindern
            const sanitized = trimmed.replace(/<[^>]*>/g, '');
            socket.emit('chatMessage', { payload: { lobbyCode, message: sanitized, userName } });
            setInput('');
        }
    };

    const leaveLobby = () => {
        if (hasLeftRef.current) return;
        socket.emit('leaveLobby', { lobbyCode, persistentId }, () => {
            hasLeftRef.current = true;
            navigate('/');
        });
    };

    const handleToggleSpectator = () => {
        socket.emit('toggleSpectator', { lobbyCode, persistentId }, (response) => {
            if (!response.success) {
                logDev('Fehler beim Ändern des Status:', response.message);
            }
        });
    };

    const handleKickUser = (targetId) => {
        if (!socket.connected) {
            console.error('Socket nicht verbunden');
            return;
        }
        socket.emit('kickUser', { lobbyCode, targetId }, (response) => {
            if (!response || !response.success) {
                console.error('Fehler beim Kicken:', response?.message || 'Unbekannter Fehler');
            }
        });
    };

    return {
        lobbyCode, isLoading, joinError,
        showJoinDialog, handleJoinDialogSubmit, forcePassword,
        users, userName, isCreator,
        showSettingsModal, setShowSettingsModal, settings,
        setSettings, copied, setCopied,
        input, setInput, sendMessage,
        leaveLobby, handleToggleSpectator, handleKickUser,
        startGame, messages, navigate
    };
}